import { useEffect, useMemo, useRef, useState } from "react";
import { userRoles } from "@/lib/constants";
import client, { apiError } from "@/api/client";
import { PageHeader, Section } from "@/components/PageChrome";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { useUserNames } from "@/hooks/useUserNames";
import { toast } from "sonner";
import { Printer, MagnifyingGlass, XCircle, CheckCircle, ArrowCounterClockwise } from "@phosphor-icons/react";

/** Returns { kind, outerPad } where kind is 'single' | 'triple' | 'default'. */
function borderKind(companyName) {
  const n = (companyName || "").toLowerCase();
  if (n.includes("goverdhan")) return "single";
  if (n.includes("parshava"))  return "triple";
  return "default";
}

/** Convert yyyy-mm-dd (or ISO datetime) to dd-mm-yyyy. Empty/invalid → "". */
function toDMY(v) {
  if (!v) return "";
  const s = String(v).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  const d = new Date(s);
  if (!isNaN(d.getTime())) {
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    return `${dd}-${mm}-${d.getFullYear()}`;
  }
  return s;
}

export default function PrintDispatchLabel() {
  const { user } = useAuth();
  const isAdmin = userRoles(user).includes("admin");
  const { nameFor } = useUserNames();
  const [retailers, setRetailers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [orders, setOrders] = useState([]);
  const [printed, setPrinted] = useState([]);
  const [orderId, setOrderId] = useState("");
  const [q, setQ] = useState("");
  const [openSug, setOpenSug] = useState(false);
  const [marking, setMarking] = useState(false);
  const [undoing, setUndoing] = useState(null);
  const searchRef = useRef(null);

  const load = async () => {
    const [{ data: r }, { data: c }, { data: o }] = await Promise.all([
      client.get("/retailers"), client.get("/companies"), client.get("/orders"),
    ]);
    setRetailers(r); setCompanies(c);
    const disp = o.filter(x => x.stage === "dispatched");
    setOrders(disp.filter(x => !x.dispatch?.label_printed_at));
    // Keep the 10 most-recently-printed (sort by label_printed_at desc)
    const p = disp.filter(x => x.dispatch?.label_printed_at)
      .sort((a, b) => (b.dispatch.label_printed_at || "").localeCompare(a.dispatch.label_printed_at || ""))
      .slice(0, 10);
    setPrinted(p);
  };
  useEffect(() => { load(); }, []);

  const suggestions = useMemo(() => {
    const s = q.trim().toLowerCase();
    const base = orders.slice(0, 200);
    if (!s) return base.slice(0, 20);
    return base.filter(o =>
      (o.order_no || "").toLowerCase().includes(s) ||
      (o.retailer_name || "").toLowerCase().includes(s)
    ).slice(0, 20);
  }, [orders, q]);

  const order = useMemo(() => orders.find(o => o.id === orderId), [orders, orderId]);
  const retailer = useMemo(() => order ? retailers.find(r => r.id === order.retailer_id) : null, [retailers, order]);
  const company = useMemo(() => order ? companies.find(c => c.id === order.company_id) : null, [companies, order]);
  const totalBoxes = Number(order?.scanning?.total_boxes || 0);
  const pageNumbers = Array.from({ length: totalBoxes || 0 }, (_, i) => i + 1);
  const canPrint = order && totalBoxes > 0;
  const kind = borderKind(company?.name);

  const pickOrder = (o) => {
    setOrderId(o.id);
    setQ(`${o.order_no} · ${o.retailer_name}`);
    setOpenSug(false);
  };

  const clearOrder = () => {
    setOrderId(""); setQ(""); setOpenSug(false);
    setTimeout(() => searchRef.current?.focus(), 0);
  };

  const markPrinted = async () => {
    if (!order) return;
    setMarking(true);
    try {
      await client.post(`/orders/${order.id}/mark-label-printed`);
      toast.success(`Marked "${order.order_no}" as printed`);
      setOrderId(""); setQ("");
      await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setMarking(false); }
  };

  return (
    <div>
      <PageHeader title="Print Dispatch Label" subtitle="A4 · 3 labels (99mm each) per page" />
      <Section>
        <div className="screen-only bg-white border border-slate-200 rounded-sm p-6 max-w-2xl">
          <div className="grid gap-4">
            <div className="relative">
              <label className="label-cap">Search order (order no or retailer name)</label>
              <div className="relative">
                <Input
                  ref={searchRef}
                  data-testid="pl-order-search"
                  value={q}
                  onChange={(e)=>{
                    setQ(e.target.value);
                    setOpenSug(true);
                    if (orderId) setOrderId("");
                  }}
                  onFocus={()=>setOpenSug(true)}
                  placeholder={orders.length ? "Start typing order no or retailer…" : "No dispatched orders pending label print"}
                  className="pr-8 rounded-sm"
                  disabled={orders.length === 0}
                />
                <div className="absolute right-2 top-1/2 -translate-y-1/2">
                  {orderId ? (
                    <button
                      type="button" data-testid="pl-order-clear"
                      onClick={clearOrder}
                      className="text-slate-400 hover:text-rose-600"
                    ><XCircle size={16}/></button>
                  ) : (
                    <MagnifyingGlass size={14} className="text-slate-400"/>
                  )}
                </div>
              </div>
              {openSug && !orderId && (
                <>
                  <div className="fixed inset-0 z-40" aria-hidden onClick={()=>setOpenSug(false)} />
                  <div
                    data-testid="pl-order-suggestions"
                    className="absolute left-0 right-0 mt-1 bg-white border border-slate-200 rounded-sm shadow-lg max-h-72 overflow-y-auto z-50"
                  >
                    {suggestions.length === 0 && (
                      <div className="px-3 py-2 text-xs text-slate-500">
                        {orders.length === 0 ? "Nothing pending label print" : `No matches for "${q}"`}
                      </div>
                    )}
                    {suggestions.map(o => (
                      <button
                        key={o.id}
                        type="button"
                        data-testid={`pl-order-suggestion-${o.id}`}
                        onClick={()=>pickOrder(o)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-orange-50 flex items-center justify-between gap-3 border-b border-slate-100 last:border-b-0"
                      >
                        <div className="min-w-0">
                          <div className="font-mono font-semibold truncate">{o.order_no}</div>
                          <div className="text-xs text-slate-500 truncate">{o.retailer_name}</div>
                        </div>
                        <div className="text-xs text-slate-700 shrink-0 font-mono">
                          {o.scanning?.total_boxes || 0} box{(o.scanning?.total_boxes || 0) === 1 ? "" : "es"}
                        </div>
                      </button>
                    ))}
                  </div>
                </>
              )}
              {orders.length === 0 && (
                <div className="text-xs text-slate-500 mt-2">All dispatched labels have been printed. Dispatch new orders to add them here.</div>
              )}
            </div>

            {order && (
              <div className="grid grid-cols-2 gap-3 text-sm bg-slate-50 border border-slate-200 rounded-sm p-4">
                <div><div className="label-cap">Company</div><div className="font-semibold">{company?.name || "—"}</div></div>
                <div><div className="label-cap">Border style</div><div>{(company?.name||"").toLowerCase().includes("goverdhan") ? "Single line" : (company?.name||"").toLowerCase().includes("parshava") ? "Triple line" : "Default"}</div></div>
                <div><div className="label-cap">Order No</div><div className="font-mono">{order.order_no}</div></div>
                <div><div className="label-cap">Total Boxes</div><div className="font-mono">{totalBoxes || <span className="text-rose-600">— missing</span>}</div></div>
                <div><div className="label-cap">Bill No</div><div className="font-mono">{order.dispatch?.bill_no || "—"}</div></div>
                <div><div className="label-cap">Bill Date</div><div className="font-mono">{toDMY(order.dispatch?.bill_date) || "—"}</div></div>
                <div><div className="label-cap">LR No</div><div className="font-mono">{order.dispatch?.lr_no || "—"}</div></div>
                <div><div className="label-cap">LR Date</div><div className="font-mono">{toDMY(order.dispatch?.lr_date) || "—"}</div></div>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <Button
                data-testid="pl-print-btn"
                onClick={() => window.print()}
                disabled={!canPrint}
                className="bg-orange-600 hover:bg-orange-700 text-white rounded-sm"
              >
                <Printer size={16} className="mr-1" weight="bold"/>
                Print {totalBoxes ? `${totalBoxes} label${totalBoxes>1?"s":""}` : ""}
              </Button>
              <Button
                data-testid="pl-mark-printed-btn"
                onClick={markPrinted}
                disabled={!order || marking}
                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm disabled:opacity-50"
              >
                <CheckCircle size={16} className="mr-1" weight="bold"/>
                {marking ? "Marking…" : "Printed Successfully"}
              </Button>
              {order && totalBoxes === 0 && (
                <span className="text-xs text-rose-600">Total Boxes not set on this order — enter in Scanning step first.</span>
              )}
            </div>
          </div>
        </div>

        {/* Admin-only "Recently marked as printed" strip with Undo */}
        {isAdmin && printed.length > 0 && (
          <div className="screen-only mt-6 bg-white border border-slate-200 rounded-sm p-4 max-w-2xl" data-testid="pl-recent-printed">
            <div className="flex items-center gap-2 mb-2">
              <ArrowCounterClockwise size={14} className="text-slate-600" weight="bold"/>
              <div className="label-cap text-slate-700">Recently marked as printed · admin only</div>
              <span className="ml-auto text-xs text-slate-500">last {printed.length}</span>
            </div>
            <div className="divide-y divide-slate-100">
              {printed.map(o => (
                <div key={o.id} className="py-2 flex items-center gap-3 text-sm" data-testid={`pl-printed-row-${o.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-mono font-semibold truncate">{o.order_no}</div>
                    <div className="text-xs text-slate-500 truncate">
                      {o.retailer_name} · marked {o.dispatch?.label_printed_at ? new Date(o.dispatch.label_printed_at).toLocaleString() : "—"}
                      {o.dispatch?.label_printed_by && ` by ${nameFor(o.dispatch.label_printed_by)}`}
                    </div>
                  </div>
                  <Button
                    data-testid={`pl-undo-${o.id}`}
                    disabled={undoing === o.id}
                    onClick={async () => {
                      if (!confirm(`Undo mark-printed for ${o.order_no}? It will reappear in the pending list.`)) return;
                      setUndoing(o.id);
                      try {
                        await client.post(`/orders/${o.id}/unmark-label-printed`);
                        toast.success(`Undone — ${o.order_no} is pending print again`);
                        await load();
                      } catch (e) { toast.error(apiError(e)); }
                      finally { setUndoing(null); }
                    }}
                    className="bg-amber-500 hover:bg-amber-600 text-white rounded-sm text-xs h-8 px-3"
                  >
                    <ArrowCounterClockwise size={12} className="mr-1" weight="bold"/>
                    {undoing === o.id ? "Undoing…" : "Undo"}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {canPrint && (
          <div className="screen-only mt-8">
            <div className="label-cap mb-2">Preview (first label)</div>
            <div className="a8-preview shadow-md">
              <LabelPage
                boxIndex={1} totalBoxes={totalBoxes}
                retailer={retailer} company={company} order={order} kind={kind}
              />
            </div>
          </div>
        )}

        <div className="print-area" id="dispatch-print-area">
          {canPrint && (() => {
            // Group labels into A4 pages of 3 labels each (3 × 99mm = 297mm = A4 height)
            const pages = [];
            for (let i = 0; i < pageNumbers.length; i += 3) {
              pages.push(pageNumbers.slice(i, i + 3));
            }
            return pages.map((group, pageIdx) => (
              <div className="a4-page" key={pageIdx}>
                {group.map((n) => (
                  <div className="label-slot" key={n}>
                    <LabelPage
                      boxIndex={n} totalBoxes={totalBoxes}
                      retailer={retailer} company={company} order={order} kind={kind}
                    />
                  </div>
                ))}
              </div>
            ));
          })()}
        </div>

        <style>{`
          /* Screen preview: single label thumbnail */
          .a8-preview { width: 210mm; height: 99mm; background: white; border: 1px solid #d4d4d8; max-width: 100%; transform-origin: top left; }
          /* A4 portrait page — 210mm × 297mm, holds 3 × 99mm label slots */
          .a4-page { width: 210mm; height: 297mm; page-break-after: always; overflow: hidden; background: white; display: flex; flex-direction: column; box-sizing: border-box; }
          .a4-page:last-child { page-break-after: auto; }
          /* Label slot spans the full A4 width */
          .label-slot { width: 100%; height: 99mm; overflow: hidden; box-sizing: border-box; }

          @media screen {
            .print-area { display: none; }
            @media (max-width: 900px) { .a8-preview { transform: scale(0.5); } }
          }
          @media print {
            @page { size: 210mm 297mm; margin: 0; }
            html, body { margin: 0 !important; padding: 0 !important; background: white !important; }
            body * { visibility: hidden !important; }
            #dispatch-print-area, #dispatch-print-area * { visibility: visible !important; }
            #dispatch-print-area { position: absolute; left: 0; top: 0; width: 210mm; display: block !important; }
            .a4-page { break-after: page; }
            .a4-page:last-child { break-after: auto; }
          }
        `}</style>
      </Section>
    </div>
  );
}

function LabelPage({ boxIndex, totalBoxes, retailer, company, order, kind }) {
  const areaLine = [retailer?.area, retailer?.city, retailer?.state, retailer?.pincode]
    .filter(Boolean).join(", ");
  const black = { color: "#000" };

  // Border layers based on company:
  //  - single (Goverdhan)   → 1 solid black border, 3px thick
  //  - triple (Parshava)    → 3 nested 1px borders with 1.5mm gaps
  //  - default              → 1 thin border
  const outerBorder = kind === "single" ? "3px solid #000" : "1px solid #000";
  const showMid   = kind === "triple";
  const showInner = kind === "triple";

  const Content = (
    <div style={{
      width: "100%", height: "100%",
      display: "flex",
      fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
      ...black,
    }}>
      {/* LEFT 60% */}
      <div style={{
        flex: "0 0 60%",
        padding: "5mm 5mm 5mm 6mm",
        borderRight: "0.6mm solid #000",
        display: "flex", flexDirection: "column", gap: "0.8mm",
        overflow: "hidden",
        ...black,
      }}>
        <div style={{ fontSize: "10pt", fontWeight: 700, ...black }}>To</div>
        <div style={{ fontSize: "22pt", fontWeight: 900, lineHeight: 1.05, ...black }}>
          {retailer?.name || "—"}
        </div>
        <div style={{ fontSize: "13pt", fontWeight: 700, lineHeight: 1.15, ...black }}>
          {retailer?.address || ""}
        </div>
        {areaLine && (
          <div style={{ fontSize: "13pt", fontWeight: 700, lineHeight: 1.15, ...black }}>
            {areaLine}
          </div>
        )}
        <div style={{ fontSize: "14pt", fontWeight: 800, marginTop: "0.5mm", ...black }}>
          Ph: {retailer?.phone || "—"}
        </div>

        <div style={{ borderTop: "0.3mm dashed #000", margin: "1mm 0" }} />

        <div style={{ fontSize: "10pt", lineHeight: 1.3, ...black }}>
          <div style={{ fontSize: "14pt", fontWeight: 900, lineHeight: 1.1, marginBottom: "0.5mm", ...black }}>
            <span style={{ fontSize: "9pt", fontWeight: 700 }}>FROM:</span> {company?.name || "—"}
          </div>
          <div>{company?.address || ""}</div>
          <div>Ph: {company?.phone || "—"}</div>
        </div>

        <div style={{ borderTop: "0.3mm dashed #000", margin: "1mm 0" }} />

        {/* Order No, Bill No, Bill Date — as requested, below company phone */}
        <div style={{
          fontSize: "11pt", lineHeight: 1.35,
          display: "flex", flexDirection: "column", gap: "0.5mm",
          ...black,
        }}>
          <div>Order No: <b>{order?.order_no || "—"}</b></div>
          <div style={{ display: "grid", gridTemplateColumns: "auto 1fr auto 1fr", columnGap: "3mm", ...black }}>
            <span>Bill No:</span>   <b>{order?.dispatch?.bill_no || "—"}</b>
            <span>Bill Date:</span> <b>{toDMY(order?.dispatch?.bill_date) || "—"}</b>
          </div>
        </div>
      </div>

      {/* RIGHT 40% */}
      <div style={{
        flex: "0 0 40%",
        padding: "3mm",
        display: "flex", flexDirection: "column", alignItems: "center",
        ...black,
      }}>
        <div style={{ fontSize: "8pt", fontWeight: 700, letterSpacing: "0.3em", textTransform: "uppercase", ...black }}>
          Box
        </div>
        {/* Big numerals — plain HTML with digit-count-aware sizing so every character is
            fully rendered inside the label, even for two-digit boxIndex and totalBoxes. */}
        {(() => {
          const nDigits = String(boxIndex).length;
          const dDigits = String(totalBoxes).length;
          // Pick font sizes (mm) that guarantee no overflow of the right 40% panel (~78mm inner).
          // Numerator stays visually dominant.
          let numMm, denMm;
          if (nDigits === 1 && dDigits === 1) { numMm = 42; denMm = 26; }
          else if (nDigits === 2 && dDigits === 1) { numMm = 32; denMm = 22; }
          else if (nDigits === 1 && dDigits === 2) { numMm = 38; denMm = 22; }
          else { numMm = 28; denMm = 20; } // both 2 digits (max "88/88")
          return (
            <div style={{
              flex: 1,
              width: "100%",
              minHeight: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
              fontWeight: 900,
              lineHeight: 1,
              color: "#000",
              overflow: "hidden",
            }}>
              <span style={{ fontSize: `${numMm}mm`, letterSpacing: "-0.05em" }} data-testid="pl-box-numerator">
                {boxIndex}
              </span>
              <span style={{ fontSize: `${denMm}mm`, letterSpacing: "-0.03em", marginLeft: "1mm" }} data-testid="pl-box-denominator">
                /{totalBoxes}
              </span>
            </div>
          );
        })()}
        <div style={{ borderTop: "0.4mm solid #000", width: "85%", margin: "1mm 0" }} />
        <div style={{
          fontSize: "8.5pt", lineHeight: 1.25, width: "100%", textAlign: "left",
          display: "flex", flexDirection: "column", gap: "0.2mm",
          ...black,
        }}>
          <SingleRow k="Transporter" v={order?.dispatch?.transporter_name} />
          <Row a={{ k: "LR", v: order?.dispatch?.lr_no }} b={{ k: "LR Dt", v: toDMY(order?.dispatch?.lr_date) }} />
          <SingleRow k="Total Pcs in this Bill" v={order?.scanning?.total_pieces} />
          <SingleRow k="Wt" v={order?.scanning?.weight} />
        </div>
      </div>
    </div>
  );

  // Wrap Content in nested borders based on kind
  let node = Content;
  if (showInner) {
    node = <div style={{ width:"100%", height:"100%", border:"1px solid #000", padding:"1mm", boxSizing:"border-box" }}>{node}</div>;
  }
  if (showMid) {
    node = <div style={{ width:"100%", height:"100%", border:"1px solid #000", padding:"1mm", boxSizing:"border-box" }}>{node}</div>;
  }
  node = <div style={{ width:"100%", height:"100%", border: outerBorder, boxSizing:"border-box", background:"white", ...black }}>{node}</div>;

  return (
    <div style={{
      width: "100%", height: "99mm",
      boxSizing: "border-box",
      padding: 0,
      background: "white",
      ...black,
    }}>
      {node}
    </div>
  );
}

function Row({ a, b }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "auto 1fr auto 1fr", gap: "1.5mm", color: "#000" }}>
      <span>{a.k}:</span><b>{a.v || "—"}</b>
      <span>{b.k}:</span><b>{b.v || "—"}</b>
    </div>
  );
}
function SingleRow({ k, v }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "1.5mm", color: "#000" }}>
      <span>{k}:</span><b>{v || "—"}</b>
    </div>
  );
}
